import { useState, useEffect, type KeyboardEvent, useMemo, useRef } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel, TextField, Tooltip, GlobalStyles, IconButton, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { FilterState, GenericOption } from './types';
import { DEFAULT_FILTERS, primaryFields } from './constants';
import FilterMultiSelectStyled from './FilterMultiSelectStyled';
import DrawerFooter from '../Common/DrawerFooter';
import CommonDrawer from '../Common/CommonDrawer';
import DrawerHeader from '../Common/DrawerHeader';
import { ADDITIONAL_FILTERS} from './constants';
import AdditionalFiltersPanel from './AdditionalFiltersPanel';

interface FilterFieldConfig {
  label: string;
  placeholder?: string;
  options: GenericOption[];
  value: GenericOption[];
  field: keyof FilterState;
  width?: number;
  pluralLabel?: string;
  isSingleSelect?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  initialOptionLimit?: number;
  loading?: boolean;
  isIndented?: boolean;
  indentLevel?: number;
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  onSaveAsDefault?: (filters: FilterState) => void;
  onResetToDefault?: () => void;
  onReset?: () => void;
  onFilterChange?: (field: keyof FilterState, value: GenericOption[]) => void;
  onDropdownOpen?: (currentFilters: FilterState, field: keyof FilterState) => void;
  currentFilters: FilterState;
  filterFields: FilterFieldConfig[];
  customFilterFields?: GenericOption[];
  hasSavedFilters?: boolean;
  savedDefaultFilters?: FilterState | null;
  applyDisabled?: boolean;
  showTagDelete?: boolean;
  hideAdditionalFilters?: boolean;
  hideMoreFilters?: boolean;
  ibpPrimaryFields?: Array<keyof FilterState | string>;
  additionalFilterOptions?: any;
  selectedAdditionalFilters?: string[];
  onToggleAdditionalFilter?: (filterId: string, checked: boolean) => void;
  onToggleAllAdditional?: (checked: boolean) => void;
  isInitialLoading?: boolean;
  isProgramDashboard?: boolean;
}

const FilterDrawer = ({
  open,
  onClose,
  onApply,
  onSaveAsDefault,
  onResetToDefault,
  onReset,
  onFilterChange,
  onDropdownOpen,
  currentFilters,
  filterFields,
  customFilterFields = [],
  hasSavedFilters = false,
  savedDefaultFilters = null,
  applyDisabled = false,
  showTagDelete = true,
  hideAdditionalFilters = false,
  hideMoreFilters = false,
  ibpPrimaryFields = [],
  additionalFilterOptions = [],
  selectedAdditionalFilters = [],
  onToggleAdditionalFilter,
  onToggleAllAdditional,
  isInitialLoading = false,
  isProgramDashboard = false,
}: FilterDrawerProps) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [isSaving, setIsSaving] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [moreFilterSelections, setMoreFilterSelections] = useState<string[]>(selectedAdditionalFilters || []);
  const moreFiltersPanelRef = useRef<HTMLDivElement>(null);
  const moreFiltersButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMoreFilters &&
        moreFiltersPanelRef.current &&
        !moreFiltersPanelRef.current.contains(event.target as Node) &&
        moreFiltersButtonRef.current &&
        !moreFiltersButtonRef.current.contains(event.target as Node)
      ) {
        setShowMoreFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreFilters]);

  useEffect(() => {
    setMoreFilterSelections(selectedAdditionalFilters || []);
  }, [selectedAdditionalFilters]);

  const COLORS = {
    background: '#E1F5FE',
    primary: '#003771',
    hover: '#39ACE7',
    light: '#EFF6FF',
    accentHover: '#81D4FA !important',
    inputBorder: '#515459ff',
    accent: "#009FDB",
    textPrimary: "#333333 !important",
    surface: "#FFFFFF",
  };

  const initialFiltersRef = useRef<FilterState>(currentFilters);

  // Sync filters on drawer open, and whenever currentFilters' actual content
  const lastSyncedFiltersJsonRef = useRef<string>(JSON.stringify(currentFilters));
  const wasOpenRef = useRef(open);

  useEffect(() => {
    const currentFiltersJson = JSON.stringify(currentFilters);
    const justOpened = open && !wasOpenRef.current;
    const contentChanged = currentFiltersJson !== lastSyncedFiltersJsonRef.current;

    if (open && (justOpened || contentChanged)) {
      setFilters(currentFilters);
      lastSyncedFiltersJsonRef.current = currentFiltersJson;
    }

    wasOpenRef.current = open;
  }, [open, currentFilters]);

  useEffect(() => {
    if (open) {
      initialFiltersRef.current = currentFilters;
    }
  }, [open]);

  // Determine if current filters differ from the base default (all "All")
  const hasFiltersChanged = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  }, [filters]);

  // Determine if current filters differ from saved default
  const filtersMatchSavedDefault = useMemo(() => {
    if (!savedDefaultFilters) return false;
    return JSON.stringify(filters) === JSON.stringify(savedDefaultFilters);
  }, [filters, savedDefaultFilters]);

  // Show "Save as Default" when filters differ from saved default (if saved defaults exist)
  // or from base default (if no saved defaults exist). This allows saving blank filters as new default.
  const showSaveAsDefault = useMemo(() => {
    if (filtersMatchSavedDefault) return false;
    if (hasSavedFilters) return !filtersMatchSavedDefault;
    return hasFiltersChanged;
  }, [hasFiltersChanged, filtersMatchSavedDefault, hasSavedFilters]);

  // Show "Reset to Default" only when saved defaults exist AND current filters differ from them
  const showResetToDefault = useMemo(() => {
    if (!hasSavedFilters || !savedDefaultFilters) return false;
    return !filtersMatchSavedDefault;
  }, [hasSavedFilters, savedDefaultFilters, filtersMatchSavedDefault]);

  // Button text: "SAVE AS DEFAULT" if no saved defaults, "SAVE AS NEW DEFAULT" if saved defaults exist
  const saveButtonText = hasSavedFilters ? 'SAVE AS NEW DEFAULT' : 'SAVE AS DEFAULT';
 
  const handleApply = () => {
    if (applyDisabled) return;

    const validFilters = {
      ...filters,
      additionalFilters: filters.additionalFilters.filter(f => f.field && f.value)
    };
    const hasChanges = JSON.stringify(validFilters) !== JSON.stringify(initialFiltersRef.current);
    if (hasChanges && onApply) onApply(validFilters);
    onClose();
  };

  const handleSaveAsDefault = async () => {
    const validFilters = {
      ...filters,
      additionalFilters: filters.additionalFilters.filter(f => f.field && f.value)
    };
    
    setIsSaving(true);
    try {
      if (onSaveAsDefault) {
        await onSaveAsDefault(validFilters);
      }
    } catch (error) {
      console.error('Error saving filters as default:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (onResetToDefault) {
      onResetToDefault();
      onClose();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (event.key === 'Enter' && !(target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      event.preventDefault();
      handleApply();
    }
  };

  const handleReset = () => {
  if (onReset) {
    onReset();
  } else {
    setFilters(DEFAULT_FILTERS);
  }
};

  const handleClose = () => {
    // Discard temp changes
    setFilters(currentFilters);
    onClose();
  };

  const handleAddCustomFilter = () => {
    const id = crypto.randomUUID();
    setFilters(prev => ({
      ...prev,
      additionalFilters: [...prev.additionalFilters, { id, field: '', value: '' }]
    }));
  };

  const handleUpdateCustomFilter = (id: string, changes: Partial<{ field: string; value: string }>) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters.map((f) =>
        f.id === id ? { ...f, ...changes } : f
      )
    }));
  };

  const handleRemoveCustomFilter = (id: string) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters.filter((f) => f.id !== id)
    }));
  };

  const normalizeMultiSelectValue = (
    newValue: GenericOption[],
    options: GenericOption[],
    previousValue: GenericOption[]
  ) => {
    const uniqueValues = Array.from(
      new Map(newValue.map(item => [item.value, item])).values()
    );

    const allOption = { label: 'All', value: 'all' };
    const hasAllNow = uniqueValues.some(item => String(item.value).toLowerCase() === 'all');
    const hadAllBefore = previousValue.some(item => String(item.value).toLowerCase() === 'all');
    const nonAllOptions = options.filter(opt => String(opt.value).toLowerCase() !== 'all');
    const nonAllSelected = uniqueValues.filter(item => String(item.value).toLowerCase() !== 'all');

    if (hadAllBefore && !hasAllNow) {
      return nonAllSelected;
    }
    if (hasAllNow && !hadAllBefore) {
      return [allOption, ...nonAllOptions];
    }
    if (nonAllOptions.length > 0 && nonAllSelected.length === nonAllOptions.length) {
      return [allOption, ...nonAllOptions];
    }
    return nonAllSelected;
  };

  // Clean up cached values that may have stale "All" selection from when fewer options existed
  const cleanupCachedValue = (value: GenericOption[], options: GenericOption[]): GenericOption[] => {
    if (!value || value.length === 0) return value;
    
    const hasAll = value.some(item => String(item.value).toLowerCase() === 'all');
    const nonAllValues = value.filter(item => String(item.value).toLowerCase() !== 'all');
    
    // If value has both "All" and specific options, remove "All" (it was auto-generated)
    if (hasAll && nonAllValues.length > 0) {
      return nonAllValues;
    }
    
    // Filter out options that no longer exist in current options list
    const validOptionValues = new Set(options.map(opt => String(opt.value).toLowerCase()));
    return value.filter(item => validOptionValues.has(String(item.value).toLowerCase()));
  };

  const handleFilterChange = (
    field: keyof FilterState,
    newValue: GenericOption[],
    options: GenericOption[]
  ) => {
    const previousValue = (filters[field] as GenericOption[]) || [];
    const normalizedValue = normalizeMultiSelectValue(newValue, options, previousValue);
    setFilters(prev => ({...prev, [field]: normalizedValue}));
    onFilterChange?.(field, normalizedValue);
  };

  const handleToggleAdditionalFilter = (filterId: string, checked: boolean) => {
    setMoreFilterSelections(prev => {
      if (filterId === 'all') {
        const allFilterIds = ADDITIONAL_FILTERS
          .filter(f => f.id !== 'all')
          .map(f => f.id);
        return checked ? allFilterIds : [];
      }
      return checked
        ? [...prev, filterId]
        : prev.filter(id => id !== filterId);
    });

    if (onToggleAdditionalFilter) {
      onToggleAdditionalFilter(filterId, checked);
    }
  };

  const handleToggleAllAdditional = (checked: boolean) => {
    const allFilterIds = ADDITIONAL_FILTERS
      .filter(f => f.id !== 'all')
      .map(f => f.id);
    setMoreFilterSelections(checked ? allFilterIds : []);
    if (onToggleAllAdditional) {
      onToggleAllAdditional(checked);
    }
  };

  const getActiveMoreFields = () => {
    const activeFields: FilterFieldConfig[] = [];
    const moreFilterFields = [
      'scheduleCategory',
      'scheduledForNext',
      'sourceCategory',
      'sourceType',
      'sourceSubcategory',
      'orchestratorScheduler',
      'dataAssetName',
      'department',
      'capability',
      'technology',
      'subscriptionName',
      'resourceGroup',
      'resourceName',
      'sustainOwner',
      'sustainProdDate'
    ];
    const isSourceSelected = moreFilterSelections.includes('sourceType');
    filterFields.forEach((field) => {
      if (moreFilterFields.includes(field.field as string) &&
        moreFilterSelections.includes(field.field as string)) {

        if ((field.field === 'sourceSubcategory' || field.field === 'orchestratorScheduler') && !isSourceSelected) {
          return;
        }

        activeFields.push(field);
      }
    });

    return activeFields;
  };

const activeMoreFields = useMemo(() => getActiveMoreFields(), [filterFields, moreFilterSelections]);
  const programField = filterFields.find(f => f.field === 'program');
  const nestedFields = filterFields.filter(f => f.isIndented === true);
  
  // For Program Dashboards, segregate sector field to show first
  const sectorFields = isProgramDashboard
    ? filterFields.filter(f => f.field === 'sector' || f.field === 'miscellaneousSectors')
    : [];
  const standardPrimaryFields = filterFields.filter((field) =>
    (primaryFields.includes(field.field as string) || ibpPrimaryFields.includes(field.field)) &&
    field.field !== 'program' && !field.isIndented &&
    !(isProgramDashboard && (field.field === 'sector' || field.field === 'miscellaneousSectors'))
  );

  return (
    <>
      <GlobalStyles
        styles={{
          '.filter-drawer-wrapper .MuiMenu-paper': {
            backgroundColor: `${COLORS.surface} !important`,
          },
          '.filter-drawer-wrapper .MuiPopover-root .MuiPaper-root': {
            backgroundColor: `${COLORS.surface} !important`,
          },
        }}
      />
      <CommonDrawer
        className='filter-drawer-wrapper'
        open={open}
        onClose={handleClose}
        backgroundColor={`${COLORS.surface} !important`}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.surface }}
          onKeyDown={handleKeyDown}
        >
          {isInitialLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'inherit',
                pointerEvents: 'auto',
              }}
            >
              <CircularProgress 
                size={48}
                sx={{
                  color: '#003771',
                }}
              />
            </Box>
          )}
          <DrawerHeader
            title='Filters'
            onClose={handleClose}
            onResetDefault={handleResetToDefault}
            onSaveAsDefault={handleSaveAsDefault}
            resetDefaultText="RESET TO DEFAULT"
            saveText={saveButtonText}
            showSaveAsDefault={showSaveAsDefault}
            showResetToDefault={showResetToDefault}
            isSaving={isSaving}
            showMoreFilters={showMoreFilters}
            onToggleMoreFilters={hideMoreFilters ? undefined : () => setShowMoreFilters((prev) => !prev)}
            moreFiltersButtonRef={hideMoreFilters ? undefined : moreFiltersButtonRef}
          />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, backgroundColor: COLORS.surface }}>
            
            {showMoreFilters && additionalFilterOptions && additionalFilterOptions.length > 0 && (
              <Box 
                ref={moreFiltersPanelRef}
                sx={{ 
                 
                }}
              >
                <AdditionalFiltersPanel
                  selectedFilters={selectedAdditionalFilters}
                  onToggleFilter={handleToggleAdditionalFilter}
                  onToggleAll={handleToggleAllAdditional}
                />
              </Box>
            )}

              {isProgramDashboard && sectorFields.map((field) => {
                const currentValue = filters[field.field] as GenericOption[] || [];
                const displayValue = cleanupCachedValue(currentValue, field.options);

                return (
                  <Box key={field.field} sx={{ mb: 2 }}>
                    <FilterMultiSelectStyled
                      label={field.label}
                      placeholder={field.placeholder}
                      options={field.options}
                      value={displayValue}
                      onChange={(newValue: GenericOption[]) =>
                        handleFilterChange(field.field, newValue, field.options)
                      }
                      width={field.width || 486}
                      pluralLabel={field.pluralLabel}
                      isSingleSelect={field.isSingleSelect}
                      disabled={field.disabled}
                      error={field.error}
                      helperText={field.errorText}
                      inputBorder={field.error ? 'transparent' : undefined}
                      initialOptionLimit={field.initialOptionLimit}
                      onOpen={() => onDropdownOpen?.(filters, field.field)}
                      loading={field.loading}
                    />
                  </Box>
                );
              })}

            {programField && (
              <Box sx={{ mb: 2 }}>
                <FilterMultiSelectStyled
                  label={programField.label}
                  placeholder={programField.placeholder}
                  options={programField.options}
                  value={cleanupCachedValue((filters[programField.field] as GenericOption[]) || [], programField.options)}
                  onChange={(newValue: GenericOption[]) =>
                    handleFilterChange(programField.field, newValue, programField.options)
                  }
                  width={programField.width || 486}
                  pluralLabel={programField.pluralLabel}
                  isSingleSelect={programField.isSingleSelect}
                  disabled={programField.disabled}
                  error={programField.error}
                  helperText={programField.errorText}
                  inputBorder={programField.error ? 'transparent' : undefined}
                  initialOptionLimit={programField.initialOptionLimit}
                  onOpen={() => onDropdownOpen?.(filters, programField.field)}
                  loading={programField.loading}
                />
              </Box>
            )}

            {nestedFields.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 , width:493}}>
                {nestedFields.map((field: FilterFieldConfig, index: number) => {
                  const currentValue = filters[field.field] as GenericOption[] || [];
                  const cleanedValue = cleanupCachedValue(currentValue, field.options);
                  const displayValue = field.isSingleSelect && cleanedValue.length === 0 
                    ? [field.options[0]] 
                    : cleanedValue;

                  let bgColor = 'transparent';
                  let borderStyle = {};
                  let indentPadding = 0;

                  if (field.isIndented) {
                    if (field.indentLevel === 2) {
                      bgColor = '#ffffff';
                      indentPadding = 3;
                      borderStyle = {
                        borderLeft: '2px solid #A9ADCB',
                        ml: 0,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: '-2px',
                          top: '0',
                          height: '100%',
                          width: '2px',
                          backgroundColor: '#A9ADCB'
                        }
                      };
                    } else {
                      bgColor = '#EFF6FF';
                      indentPadding = 2;
                      borderStyle = {
                        borderLeft: '2px solid #A9ADCB',
                        ml: 0,
                      };
                    }
                  }

                  return (
                    <Box 
                      key={field.field} 
                      sx={{ 
                        backgroundColor: bgColor,
                        pl: indentPadding,
                        py: 1,
                        ...borderStyle,
                      }}
                    >
                      <FilterMultiSelectStyled
                        label={field.label}
                        placeholder={field.placeholder}
                        options={field.options}
                        value={displayValue}
                        onChange={(newValue: GenericOption[]) =>
                          handleFilterChange(field.field, newValue, field.options)
                        }
                        width={field.width || 465}
                        pluralLabel={field.pluralLabel}
                        isSingleSelect={field.isSingleSelect}
                        disabled={field.disabled}
                        error={field.error}
                        helperText={field.errorText}
                        inputBorder={field.error ? 'transparent' : undefined}
                        initialOptionLimit={field.initialOptionLimit}
                        onOpen={() => onDropdownOpen?.(filters, field.field)}
                        loading={field.loading}
                        isNestedBg={true}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            {standardPrimaryFields.map((field) => {
              const currentValue = filters[field.field] as GenericOption[] || [];
              const cleanedValue = cleanupCachedValue(currentValue, field.options);
              const displayValue = field.isSingleSelect && cleanedValue.length === 0 
                ? [field.options[0]] 
                : cleanedValue;

              return (
                <Box key={field.field} sx={{ mb: 2 }}>
                  <FilterMultiSelectStyled
                    label={field.label}
                    placeholder={field.placeholder}
                    options={field.options}
                    value={displayValue}
                    onChange={(newValue: GenericOption[]) =>
                      handleFilterChange(field.field, newValue, field.options)
                    }
                    width={field.width || 486}
                    pluralLabel={field.pluralLabel}
                    isSingleSelect={field.isSingleSelect}
                    disabled={field.disabled}
                    error={field.error}
                    helperText={field.errorText}
                    inputBorder={field.error ? 'transparent' : undefined}
                    initialOptionLimit={field.initialOptionLimit}
                    onOpen={() => onDropdownOpen?.(filters, field.field)}
                    loading={field.loading}
                  />
                </Box>
              );
            })}

            {activeMoreFields.length > 0 && (
              <Box >
                {activeMoreFields.map((field) => {
                  const currentValue = filters[field.field] as GenericOption[] || [];
                  const cleanedValue = cleanupCachedValue(currentValue, field.options);
                  const displayValue = field.isSingleSelect && cleanedValue.length === 0 
                    ? [field.options[0]] 
                    : cleanedValue;

                  return (
                    <Box key={field.field} sx={{ mb: 2 }}>
                      <FilterMultiSelectStyled
                        label={field.label}
                        placeholder={field.placeholder}
                        options={field.options}
                        value={displayValue}
                        onChange={(newValue: GenericOption[]) =>
                          handleFilterChange(field.field, newValue, field.options)
                        }
                        width={field.width || 486}
                        pluralLabel={field.pluralLabel}
                        isSingleSelect={field.isSingleSelect}
                        disabled={field.disabled}
                        error={field.error}
                        helperText={field.errorText}
                        inputBorder={field.error ? 'transparent' : undefined}
                        initialOptionLimit={field.initialOptionLimit}
                        onOpen={() => onDropdownOpen?.(filters, field.field)}
                        loading={field.loading}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            {!hideAdditionalFilters && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.filterPanel.headerTitle }}>
                    Additional Filters
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddCustomFilter}
                    sx={{ 
                      textTransform: 'uppercase', 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      borderColor: COLORS.accent,
                      color: COLORS.accent,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: 'rgba(0, 159, 219, 0.04)'
                      }
                    }}
                  >
                    + Add Filter
                  </Button>
                </Box>

                {filters.additionalFilters.map((filter) => {
                  const shouldShowTooltip = (filter.value?.length ?? 0) > 15;
                  return (
                    <Box
                      key={filter.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr auto',
                        gap: 1,
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <FormControl fullWidth variant="outlined" sx={{ paddingTop: '3px' }}>
                        <InputLabel
                          sx={{
                            color: COLORS.textPrimary,
                            fontWeight: 400,
                            backgroundColor: 'transparent',
                            paddingX: '4px',
                            marginLeft: '-2px',
                            '&.MuiInputLabel-root': {
                              color: COLORS.textPrimary
                            },
                            '&.Mui-focused': {
                              color: COLORS.textPrimary,
                              backgroundColor: 'transparent'
                            },
                            '&.MuiInputLabel-shrink': {
                              color: 'rgba(0, 0, 0, 0.87)!important',
                              backgroundColor: 'transparent',
                              paddingX: '4px'
                            }
                          }}
                        >
                          Field
                        </InputLabel>
                        <Select
                          value={filter.field}
                          label="Field"
                          onChange={(e) => handleUpdateCustomFilter(filter.id, { field: e.target.value })}
                          IconComponent={ExpandMoreIcon}
                          MenuProps={{
                            disablePortal: true,
                          }}
                          sx={{
                            height: '44px',
                            backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,
                            borderRadius: '8px',
                            fontSize: '1rem',
                            '& .MuiSelect-select': {
                              color: COLORS.textPrimary,
                              paddingTop: '10px',
                              paddingBottom: '10px',
                              fontWeight: 400,
                            },
                            '& .MuiSelect-icon': {
                              color: `${theme.palette.filterDropdown.fieldIcon} !important`
                            },
                          }}
                        >
                          {customFilterFields.length === 0 ? (
                            <MenuItem
                              value=""
                              disabled
                              sx={{ color: COLORS.textPrimary, pointerEvents: 'none', textAlign: 'center' }}
                            >
                              No data found
                            </MenuItem>
                          ) : (
                            customFilterFields.map((option: any) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                  color: COLORS.textPrimary,
                                  fontWeight: 400,
                                  '&.Mui-selected': {
                                    backgroundColor: `${COLORS.accent} !important`, 
                                    color: COLORS.textPrimary,
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: COLORS.accentHover, 
                                  },
                                  '&:hover': {
                                    backgroundColor: COLORS.background, 
                                  },
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>

                      <Tooltip
                        title={shouldShowTooltip ? filter.value : ''}
                        placement="bottom"
                        enterDelay={500}
                        leaveDelay={200}  
                        arrow
                      >
                        <span style={{ width: '100%' }}>
                          <TextField
                            label="Value"
                            value={filter.value}
                            onChange={(e) => handleUpdateCustomFilter(filter.id, { value: e.target.value })}
                            fullWidth
                            sx={{
                              color: 'black !important',
                              backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,                   
                              '& .MuiInputBase-root': { height: '44px' },
                            }}
                            InputProps={{
                              sx: {
                                '& .MuiOutlinedInput-input': { color: `${theme.palette.text.primary} !important` }
                              }
                            }}
                            InputLabelProps={{
                              sx: {
                                color: `${theme.palette.text.primary} !important`,
                                fontWeight: 400,
                                backgroundColor: 'transparent',
                                paddingX: '4px',
                                marginLeft: '-2px',
                                "&.MuiInputLabel-root": {
                                  color: COLORS.textPrimary
                                },
                                '&.Mui-focused': {
                                  color: `${theme.palette.text.primary} !important`,
                                  backgroundColor: 'white'
                                },
                                '&.MuiInputLabel-shrink': {
                                  color: `${theme.palette.text.primary} !important`,
                                  backgroundColor: 'white',
                                  paddingX: '4px'
                                }
                              }
                            }}
                          />
                        </span>
                      </Tooltip>

                      <IconButton
                        onClick={() => handleRemoveCustomFilter(filter.id)}
                        sx={{
                          width: 35,
                          height: 35,
                          border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                          borderRadius: '4px',
                          color: `${theme.palette.inputNew.removeIcon} !important`,
                          backgroundColor: '#FFFFFF !important',
                          '&:hover': {
                            backgroundColor: `${theme.palette.inputNew.removeHoverBackground} !important`,
                            border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16, color: `${theme.palette.inputNew.removeIcon} !important` }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <DrawerFooter 
            onReset={handleReset}
            onApply={handleApply}
            applyText="APPLY"
            disabled={applyDisabled || isInitialLoading}
            resetText="CLEAR FILTERS"
          />
        </Box>
      </CommonDrawer>
    </>
  );
};

export default FilterDrawer;
